(() => {
  'use strict';

  /*
   * Rapid +/- taps in the theme currently create parallel /cart/change
   * requests. The server responses can arrive out of order and make the
   * drawer jump backward (for example 5 -> 3).
   *
   * V12 collapses rapid taps and serializes quantity requests, while leaving
   * the native CartItems rendering/event pipeline intact.
   */
  const QUANTITY_DEBOUNCE_MS = 220;

  const getQuantityCoordinator = (cartItems) => {
    if (!cartItems.__bezomeQuantityCoordinator) {
      cartItems.__bezomeQuantityCoordinator = {
        lines: new Map(),
        activeRequest: false,
      };
    }

    return cartItems.__bezomeQuantityCoordinator;
  };

  const scheduleNextQuantityRequest = (cartItems) => {
    const coordinator = getQuantityCoordinator(cartItems);

    if (coordinator.activeRequest || !cartItems.isConnected) {
      return;
    }

    const readyEntry = [...coordinator.lines.values()].find(
      (entry) => entry.ready && !entry.inFlight && entry.latest
    );

    if (!readyEntry) {
      return;
    }

    sendQuantityRequest(cartItems, readyEntry);
  };

  const sendQuantityRequest = async (cartItems, entry) => {
    const coordinator = getQuantityCoordinator(cartItems);

    if (
      coordinator.activeRequest ||
      entry.inFlight ||
      !entry.ready ||
      !entry.latest ||
      !cartItems.isConnected
    ) {
      return;
    }

    const request = {
      ...entry.latest,
    };

    entry.ready = false;
    entry.inFlight = true;
    entry.sentQuantity = request.quantity;
    coordinator.activeRequest = true;

    /*
     * Preserve the theme's existing loading treatment.
     */
    try {
      cartItems.showLoader(request.line);
    } catch (error) {
      // Loader failure must never block a cart update.
    }

    let sectionsToBundle = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: {
          sections: sectionsToBundle,
        },
      })
    );

    const body = JSON.stringify({
      line: request.line,
      quantity: request.quantity,
      sections: sectionsToBundle,
    });

    try {
      const response = await fetch(
        `${FoxTheme.routes.cart_change_url}`,
        {
          ...FoxTheme.utils.fetchConfig(),
          body,
        }
      );

      const parsedState = await response.json();

      /*
       * A newer click happened while this request was in flight.
       *
       * Shopify has already processed the old request, but we deliberately DO
       * NOT publish its stale section HTML into the drawer. Instead we
       * immediately send the newest requested quantity.
       *
       * This prevents visual sequences such as:
       * 1 -> 2 -> fireworks -> 3 -> fireworks -> 5 -> 3
       */
      const latest = entry.latest;
      const superseded =
        latest &&
        Number(latest.quantity) !== Number(request.quantity);

      if (superseded) {
        if (entry.timer) {
          window.clearTimeout(entry.timer);
          entry.timer = null;
        }

        entry.ready = true;
      } else {
        /*
         * Final/current response: pass it through the theme's ORIGINAL pub/sub
         * pipeline so all existing drawer rendering, totals, focus handling,
         * cart:updated events remain unchanged.
         */
        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          {
            sources: 'cart-items',
            cart: parsedState,
            target: request.target,
            line: request.line,
            name: request.name,
          }
        );

        coordinator.lines.delete(entry.key);
      }
    } catch (error) {
      console.error('Bezome quantity update failed:', error);

      /*
       * If the user already requested a newer value, still allow that latest
       * value to try once the failed request is released.
       */
      const latest = entry.latest;
      const hasNewerValue =
        latest &&
        Number(latest.quantity) !== Number(request.quantity);

      if (hasNewerValue) {
        entry.ready = true;
      } else {
        coordinator.lines.delete(entry.key);
      }
    } finally {
      entry.inFlight = false;
      coordinator.activeRequest = false;

      /*
       * Use a microtask so a stale response can never recursively stack
       * requests in the same JS call frame.
       */
      Promise.resolve().then(() => {
        scheduleNextQuantityRequest(cartItems);
      });
    }
  };

  const installQuantityUpdateGuard = () => {
    const CartItemsClass = window.customElements?.get('cart-items');

    if (!CartItemsClass) {
      window.customElements
        ?.whenDefined('cart-items')
        .then(installQuantityUpdateGuard)
        .catch(() => {});
      return;
    }

    const prototype = CartItemsClass.prototype;

    if (
      !prototype ||
      prototype.__bezomeQuantityGuardInstalled
    ) {
      return;
    }

    prototype.__bezomeQuantityGuardInstalled = true;
    prototype.__bezomeOriginalUpdateQuantity =
      prototype.updateQuantity;

    prototype.updateQuantity = function (
      line,
      quantity,
      name,
      target
    ) {
      const normalizedLine = String(line);
      const normalizedQuantity =
        Number.parseInt(quantity, 10);

      /*
       * Fall back to the theme's original method if an unexpected value gets
       * through. This keeps V12 conservative.
       */
      if (
        !normalizedLine ||
        Number.isNaN(normalizedQuantity)
      ) {
        const originalUpdateQuantity =
          Object.getPrototypeOf(this)
            ?.__bezomeOriginalUpdateQuantity;

        return originalUpdateQuantity
          ?.call(this, line, quantity, name, target);
      }

      const coordinator = getQuantityCoordinator(this);

      let entry = coordinator.lines.get(normalizedLine);

      if (!entry) {
        entry = {
          key: normalizedLine,
          latest: null,
          timer: null,
          ready: false,
          inFlight: false,
          sentQuantity: null,
        };

        coordinator.lines.set(normalizedLine, entry);
      }

      /*
       * Always keep only the user's newest intended quantity.
       */
      entry.latest = {
        line,
        quantity: normalizedQuantity,
        name,
        target,
      };

      if (entry.timer) {
        window.clearTimeout(entry.timer);
        entry.timer = null;
      }

      /*
       * Removal should stay immediate. Normal +/- taps get a short debounce so
       * 1 -> 2 -> 3 -> 4 -> 5 becomes ONE Shopify request for quantity 5.
       */
      const delay =
        normalizedQuantity === 0
          ? 0
          : QUANTITY_DEBOUNCE_MS;

      entry.timer = window.setTimeout(() => {
        entry.timer = null;
        entry.ready = true;
        scheduleNextQuantityRequest(this);
      }, delay);
    };
  };

  installQuantityUpdateGuard();
})();
